import { Component, Injector, Input } from '@angular/core';
import { BaseComponent } from 'src/app/shared/baseComponent';

enum ModifierState {
  Default,
  PathInstancer,
  Grass,
  Water,
  ComponentCopy
}

@Component({
  selector: 'app-modifiers',
  templateUrl: './modifiers.component.html',
  styleUrls: ['./modifiers.component.css']
})
export class ModifiersComponent extends BaseComponent {
  @Input() scenario: any;
  @Input() saveFn?: () => Promise<void>;
  @Input() reloadFn?: () => Promise<void>;

  // UI state
  showModifier = false;
  showModifierPathInstancer = false;
  showModifierGrass = false;
  showModifierWater = false;
  showModifierComponentCopy = false;
  showJsonError = false;
  modifierState: ModifierState = ModifierState.Default;

  // current modifier being edited
  modifier: any = {};
  modifiertext = '';
  modifierIndex = 0;
  modifierclass = '';
  modifierseqno: any = 10;
  modifiercomponent: string = '';
  modifiername: string = '';
  modifiercommands: string = '';
  modifierForewardAxis: string = '+X';
  modifierStepSize: string = '2.55';
  modifierOriginOffsetX: string = '0';
  modifierOriginOffsetZ: string = '0';
  modifierRotationOffsetDeg: string = '0';
  componentCoordinatesText: string = '';

  parsedCommands: { token: string; meaning: string; value: string }[] = [];
  commandParseRemainder: string = '';
  showCommandPreview: boolean = false;

  private formatTo2(value: any): string {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  }

  parseCommandString(): void {
    this.parsedCommands = [];
    this.commandParseRemainder = '';
    const input = this.modifiercommands || '';
    const regex = /(ss|s|r|m|o|d|f)(-?\d+(\.\d+)?)/g;
    const meanings: Record<string, string> = {
      ss: 'Set step size',
      s: 'Create instances',
      r: 'Rotate direction and object',
      m: 'Move without creating instances',
      o: 'Orient object (no direction change)',
      d: 'Change movement direction only',
      f: 'Flex turn',
    };
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(input)) !== null) {
      if (match.index > lastIndex) {
        this.commandParseRemainder += input.slice(lastIndex, match.index);
      }
      this.parsedCommands.push({
        token: match[0],
        meaning: meanings[match[1]] || match[1],
        value: match[2],
      });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < input.length) {
      this.commandParseRemainder += input.slice(lastIndex);
    }
    this.showCommandPreview = true;
  }

  // shared helpers (reused UI)
  patch: any = { xPos: 0, zPos: 0, radius: 1, nBlades: 50 };
  patchIndex = -1;
  inventoryItemIndex = -1;
  inventoryItem: any = {};
  showComponentList = false;

  constructor(injector: Injector) {
    super(injector);
  }

  // create new empty modifier entry and open editor
  addModifier() {
    if (!this.scenario.modifiers) { this.scenario.modifiers = []; }
    // derive next seqNo
    let nextSeq = 10;
    try {
      const seqs = this.scenario.modifiers.map((m: any) => Number(m.seqNo) || 0);
      if (seqs.length > 0) nextSeq = Math.max(...seqs) + 10;
    } catch { }
  const base: any = { class: 'Modifier', enabled: true, component: '', name: '', seqNo: nextSeq };
    this.scenario.modifiers.push(base);
    this.editModifier(base, this.scenario.modifiers.length - 1);
  }

  // table actions
  editModifier(mod: any, index: number) {
    if (mod == null) {
      this.modifier = { class: 'Modifier', enabled: true, component: '', name: '', seqNo: 10 };
      if (!this.scenario.modifiers) { this.scenario.modifiers = []; }
      this.scenario.modifiers.push(this.modifier);
      this.modifierIndex = this.scenario.modifiers.length - 1;
    } else {
      this.modifier = mod;
      this.modifierIndex = index;
    }
    // migrate legacy property names if present
    if (!this.modifier.component && (this.modifier as any).componentname) {
      this.modifier.component = (this.modifier as any).componentname;
    }
    
    this.modifierclass = this.modifier.class;
    this.modifierseqno = this.modifier.seqNo;
    this.modifiercomponent = this.modifier.component || '';
    this.modifiername = this.modifier.name || '';
    this.modifiercommands = this.modifier.commands || '';
    this.modifierForewardAxis = this.modifier.forewardAxis || '+X';
    this.modifierStepSize = String(this.modifier.stepSize ?? 2.55);
    this.modifierOriginOffsetX = String(this.modifier.originOffsetX ?? 0);
    this.modifierOriginOffsetZ = String(this.modifier.originOffsetZ ?? 0);
    this.modifierRotationOffsetDeg = String(this.modifier.rotationOffsetDeg ?? 0);
    this.componentCoordinatesText = '';
    this.modifiertext = JSON.stringify(this.modifier, null, 2);

    const cls = (this.modifier.class || '').trim();
    
    // Initialize default values for specific modifier types if missing
    if ((cls === 'Water' || cls === 'WaterModel') && !this.modifier.waterMethod) {
      this.modifier.waterMethod = 'creek';
    }
    switch (cls) {
      case 'PathInstancer':
      case 'PathInstancerModel':
        this.modifierState = ModifierState.PathInstancer; break;
      case 'Grass':
      case 'GrassModel':
        this.modifierState = ModifierState.Grass; break;
      case 'Water':
      case 'WaterModel':
        this.modifierState = ModifierState.Water; break;
      case 'ComponentCopy':
      case 'ComponentCopyModel':
        this.modifierState = ModifierState.ComponentCopy; break;
      default:
        this.modifierState = ModifierState.Default; break;
    }
    if (this.modifierState === ModifierState.ComponentCopy && Array.isArray(this.modifier.copies)) {
      this.modifier.copies = this.modifier.copies.map((copy: any) => ({
        ...copy,
        scale: copy.scale ?? 1,
      }));
    }
    this.showModifier = this.modifierState === ModifierState.Default;
    this.showModifierPathInstancer = this.modifierState === ModifierState.PathInstancer;
    this.showModifierGrass = this.modifierState === ModifierState.Grass;
    this.showModifierWater = this.modifierState === ModifierState.Water;
    this.showModifierComponentCopy = this.modifierState === ModifierState.ComponentCopy;
  }

  async deleteModifier(_i: any, index: number) {
    this.loader.show();
    try {
      this.scenario.modifiers.splice(index, 1);
      if (this.saveFn) { await this.saveFn(); }
      if (this.reloadFn) { await this.reloadFn(); }
    } finally {
      this.loader.hide();
    }
  }

  // save handlers
  async saveModifierFromJsonString() {
    this.showJsonError = false;
    try {
      const inter = JSON.parse(this.modifiertext);
      this.modifier = inter;
      this.scenario.modifiers[this.modifierIndex] = this.modifier;
  // re-open appropriate modal after parse for continuity
  this.editModifier(this.modifier, this.modifierIndex);
      if (this.saveFn) { await this.saveFn(); }
      if (this.reloadFn) { await this.reloadFn(); }
    } catch (_e) {
      this.showJsonError = true;
    } finally {
      this.loader.hide();
    }
  }

  async saveModifier() {
    this.loader.show();
    this.showJsonError = false;
    try {
      // Copy editable fields back to underlying object
      this.modifier.class = this.modifierclass || this.modifier.class || '';
      this.modifier.seqNo = Number(this.modifierseqno) || 0;
      this.modifier.component = this.modifiercomponent;
      this.modifier.name = this.modifiername;
      if (this.modifierState === ModifierState.PathInstancer) {
        (this.modifier as any).commands = this.modifiercommands || '';
        (this.modifier as any).forewardAxis = this.modifierForewardAxis || '+X';
        (this.modifier as any).stepSize = Number(this.modifierStepSize) || 2.55;
        (this.modifier as any).originOffsetX = Number(this.modifierOriginOffsetX) || 0;
        (this.modifier as any).originOffsetZ = Number(this.modifierOriginOffsetZ) || 0;
        (this.modifier as any).rotationOffsetDeg = Number(this.modifierRotationOffsetDeg) || 0;
      } else {
        if ('commands' in this.modifier && !this.modifiercommands) delete (this.modifier as any).commands;
      }
      this.scenario.modifiers[this.modifierIndex] = this.modifier;
      try { this.modifiertext = JSON.stringify(this.modifier, null, 2); } catch {}
  this.showModifier = false;
  this.showModifierPathInstancer = false;
  this.showModifierGrass = false;
  this.showModifierWater = false;
  this.showModifierComponentCopy = false;
      if (this.saveFn) { await this.saveFn(); }
      if (this.reloadFn) { await this.reloadFn(); }
    } catch (_e) {
      this.showJsonError = true;
    } finally {
      this.loader.hide();
    }
  }

  // Methods for managing ComponentCopy copies
  addCopy(): void {
    if (!this.modifier.copies) {
      this.modifier.copies = [];
    }
    this.modifier.copies.push({
      name: `Copy${this.modifier.copies.length + 1}`,
      posX: 0,
      posY: 0,
      posZ: 0,
      rotX: 0,
      rotY: 0,
      rotZ: 0,
      scale: 1
    });
  }

  removeCopy(index: number): void {
    if (this.modifier.copies && this.modifier.copies.length > 0) {
      this.modifier.copies.splice(index, 1);
    }
  }

  setCoordinates(): void {
    const key = (this.modifiercomponent || '').trim();
    const components = this.scenario?.components || [];

    if (!key) {
      this.componentCoordinatesText = 'No modifier component value provided.';
      return;
    }

    const normalizedKey = key.toLowerCase();
    let component = components.find((c: any) => (c?.id || '') === key || (c?.name || '') === key);

    if (!component) {
      component = components.find((c: any) =>
        String(c?.id || '').toLowerCase() === normalizedKey ||
        String(c?.name || '').toLowerCase() === normalizedKey
      );
    }

    if (!component) {
      this.componentCoordinatesText = `Component '${key}' not found in scenario components.`;
      return;
    }

    const x = component.posX ?? component.xPos ?? component.x ?? 0;
    const y = component.posY ?? component.yPos ?? component.y ?? 0;
    const z = component.posZ ?? component.zPos ?? component.z ?? 0;
    const rx = component.rotX ?? component.rx ?? component.rotationX ?? 0;
    const ry = component.rotY ?? component.ry ?? component.rotationY ?? 0;
    const rz = component.rotZ ?? component.rz ?? component.rotationZ ?? 0;

    this.componentCoordinatesText =
      `Coords: X: ${this.formatTo2(x)}, Y: ${this.formatTo2(y)}, Z: ${this.formatTo2(z)}\n` +
      `Rot: RX: ${this.formatTo2(rx)}, RY: ${this.formatTo2(ry)}, RZ: ${this.formatTo2(rz)}`;
  }

  trackByCopyIndex(index: number, item: any): number {
    return index;
  }
}
